<template>
    <v-container fluid>
        <v-card class="elevation-0">
            <v-card-title class="warning white--text">
                <span>Materiais</span>
                <v-spacer />
                <v-btn small color="primary" dark @click="openDialog()">
                    <v-icon left small>mdi-plus</v-icon>
                    Adicionar
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-0">
                <v-simple-table fixed-header>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Frente Obra</th>
                                <th>Classe</th>
                                <th>Artigo</th>
                                <th>Un</th>
                                <th>QTD</th>
                                <th>Local</th>
                                <th class="text-right">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in fields.materiais" :key="item.id || index">
                                <td>{{ frenteText(item.frente_id) }}</td>
                                <td>{{ classeText(item) }}</td>
                                <td>{{ materialText(item) }}</td>
                                <td>{{ materialUnity(item) }}</td>
                                <td>{{ valueOrDash(item.qty) }}</td>
                                <td>{{ valueOrDash(item.local) }}</td>
                                <td class="text-right text-no-wrap">
                                    <v-btn icon small color="primary" @click="openDialog(index)">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon small color="error" @click="deleteMaterial(index)">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="fields.materiais.length === 0">
                                <td colspan="7" class="text-center grey--text py-6">
                                    Sem materiais.
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialog" max-width="850" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    {{ editIndex === null ? 'Adicionar Material' : 'Editar Material' }}
                </v-card-title>

                <v-card-text class="pt-6">
                    <v-progress-linear
                        v-if="loadingCatalog"
                        class="mb-5"
                        color="primary"
                        indeterminate
                    />

                    <v-alert v-if="dialogError" type="error" dense text>
                        {{ dialogError }}
                    </v-alert>

                    <v-row>
                        <v-col v-if="!hideFrentes" cols="12" md="6">
                            <v-autocomplete
                                v-model="draft.frente_id"
                                :items="frentes_obra"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Frente de Obra *"
                                outlined
                                dense
                                @change="onFrenteChange"
                            />
                        </v-col>

                        <v-col v-if="classeNeeded" cols="12" md="6">
                            <v-autocomplete
                                v-model="draft.classe_id"
                                :items="availableClasses"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Classe *"
                                outlined
                                dense
                                @change="draft.material = null"
                            />
                        </v-col>

                        <v-col cols="12">
                            <v-autocomplete
                                v-model="draft.material"
                                :items="availableMaterials"
                                :item-text="item => item.artigo + ' - ' + item.descricao"
                                item-value="id"
                                label="Material *"
                                outlined
                                dense
                            />
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-text-field
                                :value="selectedUnity"
                                label="Unidade"
                                outlined
                                dense
                                disabled
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="draft.qty"
                                label="Quantidade"
                                type="number"
                                step="0.01"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="draft.local"
                                label="Local"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeDialog">Cancelar</v-btn>
                    <v-btn color="primary" dark @click="saveLine">Gravar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Materiais from '@/api/Materiais.js'
import Classe from '@/api/Classe.js'
import dayjs from 'dayjs'

export default {
    name: 'Materials',

    props: {
        fields: {
            type: Object,
            required: true
        },
        frentes_obra: {
            type: Array,
            default: () => []
        },
        hideFrentes: {
            type: Boolean,
            default: false
        },
        template_classes: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            materiais: [],
            catalogLoaded: false,
            loadingCatalog: false,

            dialog: false,
            editIndex: null,
            draft: {},
            availableClasses: [],
            classeNeeded: false,
            dialogError: null
        }
    },

    computed: {
        availableMaterials() {
            if (!this.draft || !this.draft.classe_id) return this.materiais

            const selectedClass = this.availableClasses.find(
                item => String(item.ID) === String(this.draft.classe_id)
            )

            const classeId = selectedClass ? selectedClass.ClasseId : null
            if (!classeId) return this.materiais

            return this.materiais.filter(item =>
                String(item.classe_id) === String(classeId) ||
                (item.classe_id === null && item.frente_id === null)
            )
        },

        selectedMaterial() {
            return this.materiais.find(
                item => String(item.id) === String(this.draft.material)
            )
        },

        selectedUnity() {
            if (!this.selectedMaterial) return '-'
            return this.selectedMaterial.unidade_base || this.selectedMaterial.unidade || '-'
        }
    },

    methods: {
        async ensureCatalog() {
            if (this.catalogLoaded) return

            this.loadingCatalog = true
            try {
                const response = await Materiais.getList({
                    id_frente_obra: this.fields.frente_id,
                    id_obra_classe: this.fields.classe_id == null ? null : '1',
                    obra_id: this.fields.obra_id,
                    all: true
                })

                this.materiais = response.data || []
                this.catalogLoaded = true
            } finally {
                this.loadingCatalog = false
            }
        },

        async openDialog(index = null) {
            this.editIndex = index
            this.dialogError = null
            this.availableClasses = []
            this.classeNeeded = false

            const source = index === null ? null : this.fields.materiais[index]

            this.draft = source
                ? this.clone(source)
                : {
                    id: `${dayjs().valueOf()}-new`,
                    material: null,
                    frente_id: null,
                    classe_id: null,
                    qty: 0,
                    local: null,
                    options: ''
                }

            try {
                await this.ensureCatalog()
                await this.prepareClasses()
                this.dialog = true
            } catch (error) {
                this.dialogError = 'Não foi possível carregar os materiais.'
                this.dialog = true
            }
        },

        closeDialog() {
            this.dialog = false
            this.dialogError = null
        },

        async onFrenteChange() {
            this.draft.classe_id = null
            this.draft.material = null
            await this.prepareClasses()
        },

        async prepareClasses() {
            if (this.hideFrentes) {
                this.availableClasses = this.template_classes || []
                this.classeNeeded = this.availableClasses.length > 0
                return
            }

            if (!this.draft.frente_id) {
                this.availableClasses = []
                this.classeNeeded = false
                return
            }

            const frente = this.frentes_obra.find(
                item => String(item.ID) === String(this.draft.frente_id)
            )

            if (!frente) return

            if (Array.isArray(frente.classes) && frente.classes.length > 0) {
                this.availableClasses = frente.classes
            } else {
                const response = await Classe.getClasses({ SubEmp: frente.SubEmp })
                this.availableClasses = response.data || []
            }

            this.classeNeeded = this.availableClasses.length > 0
        },

        saveLine() {
            this.dialogError = null

            if (!this.draft.material) {
                this.dialogError = 'Seleciona um material.'
                return
            }

            if (!this.hideFrentes && !this.draft.frente_id) {
                this.dialogError = 'Seleciona uma frente de obra.'
                return
            }

            if (this.classeNeeded && !this.draft.classe_id) {
                this.dialogError = 'Seleciona uma classe.'
                return
            }

            const selected = this.selectedMaterial
            const row = {
                ...this.clone(this.draft),
                artigo: selected ? selected.artigo : this.draft.artigo,
                descricao: selected ? selected.descricao : this.draft.descricao,
                un: selected ? (selected.unidade_base || selected.unidade || '-') : (this.draft.un || '-'),
                classeNeeded: this.classeNeeded,
                available_classes: this.clone(this.availableClasses)
            }

            if (this.editIndex === null) this.fields.materiais.push(row)
            else this.$set(this.fields.materiais, this.editIndex, row)

            this.closeDialog()
        },

        deleteMaterial(index) {
            this.fields.materiais.splice(index, 1)
        },

        materialText(item) {
            if (!item) return '-'

            const selected = this.materiais.find(
                material => String(material.id) === String(item.material)
            )

            if (selected) return `${selected.artigo} - ${selected.descricao}`
            if (item.artigo || item.descricao) {
                return `${item.artigo || ''}${item.artigo && item.descricao ? ' - ' : ''}${item.descricao || ''}`
            }

            return this.valueOrDash(item.material)
        },

        materialUnity(item) {
            if (!item) return '-'
            if (item.un) return item.un

            const selected = this.materiais.find(
                material => String(material.id) === String(item.material)
            )

            return selected ? (selected.unidade_base || selected.unidade || '-') : '-'
        },

        frenteText(id) {
            if (!id) return '-'
            const frente = this.frentes_obra.find(item => String(item.ID) === String(id))
            return frente ? frente.Descricao : id
        },

        classeText(item) {
            if (!item || !item.classe_id) return '-'
            const pools = [item.available_classes || [], this.template_classes || []]

            for (const pool of pools) {
                const found = pool.find(c => String(c.ID) === String(item.classe_id))
                if (found) return found.Descricao
            }

            return item.classe_id
        },

        valueOrDash(value) {
            return value === null || value === undefined || value === '' ? '-' : value
        },

        clone(value) {
            return JSON.parse(JSON.stringify(value))
        }
    }
}
</script>
